import {
  Select as UIKitSelect,
  type SelectProps as UIKitSelectProps,
} from "@circle-react-uikit/Select";
import { useFilteredItems } from "@circle-react-uikit/Select/useFilteredItems";
import { useInputController } from "@circle-react-uikit/Select/useInputController";
import { MultiSelect as CompassMultiSelect } from "@circleco/compass/components/MultiSelect";
import { Select as CompassSelect } from "@circleco/compass/components/Select";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import { omit, uniqBy } from "lodash";
import { useMemo, useRef, useState, type ReactNode } from "react";

import { t } from "@/i18n-js/instance";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

const I18N_ROOT = "compass_compat.select";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Re-export for compatibility
export type { SelectProps } from "@circle-react-uikit/Select";

type PrimitiveValue = number | string;

interface LegacyOption {
  disabled?: boolean;
  listItemText?: ReactNode;
  prefix?: ReactNode;
  text: string;
  value: PrimitiveValue;
}

interface CompassOption {
  disabled?: boolean;
  label: string;
  prefix?: ReactNode;
  value: PrimitiveValue;
}

interface CompassCompatSelectProps extends UIKitSelectProps {
  description?: ReactNode;
  error?: string;
  invalid?: boolean;
  isSearchable?: boolean;
  label?: ReactNode;
  layout?: "horizontal" | "vertical";
  required?: boolean;
  rootProps?: Record<string, unknown>;
}

const normalizeOptions = (data: unknown): LegacyOption[] => {
  if (!Array.isArray(data)) return [];
  return uniqBy(data as LegacyOption[], "value");
};

const toCompassOption = (option: LegacyOption): CompassOption => ({
  disabled: option.disabled,
  label: option.text,
  prefix: option.prefix,
  value: option.value,
});

const toCompassOptions = (options: LegacyOption[]): CompassOption[] =>
  options.map(toCompassOption);

const hasCustomOptionRendering = (options: LegacyOption[]) =>
  options.some(option => option.listItemText != null);

const isPrimitiveValue = (value: unknown): value is PrimitiveValue =>
  typeof value === "string" || typeof value === "number";

const resolveCreatableOption = (
  addable: CompassCompatSelectProps["addable"],
  inputValue: string,
): CompassOption | null => {
  const trimmed = inputValue.trim();
  if (!trimmed || !addable) return null;

  if (addable === true) {
    return { label: `Add ${trimmed}`, value: trimmed };
  }

  const option = addable(trimmed);
  if (typeof option?.text !== "string" || !isPrimitiveValue(option.value)) {
    return null;
  }
  return { label: option.text, value: option.value };
};

export function Select(props: Readonly<CompassCompatSelectProps>) {
  const {
    data,
    filterBy,
    shouldFilterSelectedItems = false,
    isSearchable: isSearchableProp,
  } = props;
  const isCircleV4 = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );
  const legacyOptions = useMemo(() => normalizeOptions(data), [data]);
  const { value, onChange } = useInputController(props) as {
    onChange: ((event: { value: unknown }) => void) | undefined;
    value: PrimitiveValue[];
  };
  const [searchValue, setSearchValue] = useState("");
  const filteredOptions = useFilteredItems(
    legacyOptions,
    filterBy,
    searchValue,
    shouldFilterSelectedItems,
    value,
  ) as LegacyOption[];
  const allCompassOptions = useMemo<CompassOption[]>(
    () => toCompassOptions(legacyOptions),
    [legacyOptions],
  );
  const compassOptions = useMemo<CompassOption[]>(
    () => toCompassOptions(filteredOptions),
    [filteredOptions],
  );

  const selectedOptionsCacheRef = useRef<CompassOption[]>(
    allCompassOptions.filter(option => value.includes(option.value)),
  );
  const rememberSelectedOption = (option: CompassOption) => {
    selectedOptionsCacheRef.current = uniqBy(
      [...selectedOptionsCacheRef.current, option],
      "value",
    );
  };

  const selectedOptions = useMemo<CompassOption[]>(() => {
    if (value.length === 0) return [];
    const pool = uniqBy(
      [...selectedOptionsCacheRef.current, ...allCompassOptions],
      "value",
    );
    return value
      .map(primitive => pool.find(option => option.value === primitive))
      .filter((option): option is CompassOption => option !== undefined);
  }, [value, allCompassOptions]);

  if (!isCircleV4 || hasCustomOptionRendering(legacyOptions)) {
    return <UIKitSelect {...omit(props, ["layout", "rootProps"])} />;
  }

  const {
    addable,
    dataTestId,
    direction = "down",
    disabled: isDisabled = false,
    hideSelectedItems: shouldHideSelectedItems = false,
    inputContainerClassName,
    name,
    notFoundContent,
    onSearch,
    placeholder,
    required: isRequired,
    label,
    description,
    error,
    invalid: isInvalid,
    layout,
    variant,
    rootProps,
  } = props;
  const isMultiple = variant === "tags";

  const compassLayout: "horizontal" | undefined =
    layout === "horizontal" ? "horizontal" : undefined;

  const side: "bottom" | "top" = direction === "up" ? "top" : "bottom";
  const i18n = {
    creatableLabel: resolveCreatableOption(addable, searchValue)?.label,
    searchPlaceholder: placeholder ?? t([I18N_ROOT, "search_placeholder"]),
  };

  const handleInputValueChange = (nextValue: string) => {
    if (nextValue === searchValue) return;
    setSearchValue(nextValue);
    void onSearch?.(nextValue);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) void onSearch?.("");
  };

  const compassCommonProps = {
    rootProps,
    "data-testid": dataTestId,
    className: inputContainerClassName,
    description,
    disabled: isDisabled,
    error,
    invalid: isInvalid,
    label,
    layout: compassLayout,
    name,
    emptyMessage: notFoundContent ?? t("no_options"),
    placeholder: placeholder ?? t("select_an_option"),
    required: isRequired,
    side,
  };

  if (isMultiple) {
    const handleMultiValueChange = (nextOptions: CompassOption[]) => {
      nextOptions.forEach(rememberSelectedOption);
      if (shouldHideSelectedItems) {
        const appendedValues = nextOptions
          .map(option => option.value)
          .filter(nextValue => !value.includes(nextValue));
        if (appendedValues.length > 0) {
          onChange?.({ value: [...value, ...appendedValues] });
        }
      } else {
        onChange?.({ value: nextOptions.map(option => option.value) });
      }
    };

    const handleMultiCreate = (inputValue: string) => {
      const option = resolveCreatableOption(addable, inputValue);
      if (!option) return;
      rememberSelectedOption(option);
      if (!value.includes(option.value)) {
        onChange?.({ value: [...value, option.value] });
      }
    };

    return (
      <CompassMultiSelect<PrimitiveValue>
        {...compassCommonProps}
        className={allowedClassesFor(
          "MultiSelect",
          compassCommonProps.className ?? "",
        )}
        options={compassOptions}
        value={shouldHideSelectedItems ? [] : selectedOptions}
        onValueChange={handleMultiValueChange}
        onInputValueChange={handleInputValueChange}
        onOpenChange={handleOpenChange}
        i18n={i18n}
        creatable={Boolean(addable)}
        onCreateOption={handleMultiCreate}
        align="start"
      />
    );
  }

  const selectedOption = selectedOptions[0] ?? null;
  const isSearchable = Boolean(
    filterBy || onSearch || addable || isSearchableProp,
  );

  const handleSingleValueChange = (option: CompassOption | null) => {
    if (option) rememberSelectedOption(option);
    onChange?.({ value: option?.value ?? "" });
  };

  const handleSingleCreate = (inputValue: string) => {
    const option = resolveCreatableOption(addable, inputValue);
    if (!option) return;
    rememberSelectedOption(option);
    onChange?.({ value: option.value });
  };

  return (
    <CompassSelect<PrimitiveValue>
      {...compassCommonProps}
      className={allowedClassesFor(
        "Select",
        compassCommonProps.className ?? "",
      )}
      options={compassOptions}
      value={selectedOption}
      searchable={isSearchable}
      onValueChange={handleSingleValueChange}
      onInputValueChange={isSearchable ? handleInputValueChange : undefined}
      onOpenChange={isSearchable ? handleOpenChange : undefined}
      i18n={i18n}
      creatable={Boolean(addable)}
      onCreateOption={handleSingleCreate}
      align="start"
    />
  );
}

Select.displayName = "Select";
